import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { membersAPI, paymentsAPI, invoiceAPI, feeConfigAPI, paymentMethodsAPI, paymentSlipsAPI, settingsAPI } from '../../services/api';

function calcAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function findBracket(brackets, age) {
  if (age == null) return null;
  return brackets.find(b => age >= Number(b.age_from) && age <= Number(b.age_to)) || null;
}

function isPaid(p) { return String(p?.paystatus || '').toUpperCase() === 'PAID'; }
function statusBadge(p) {
  return isPaid(p) ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
}
const ITEMS_PER_PAGE = 8;

// ─── Generate Invoice Modal ──────────────────────────────────────────────────

function GenerateModal({ onClose, onDone, myMember }) {
  const [step, setStep]               = useState('choose'); // choose | group-input | done
  const [result, setResult]           = useState(null);
  const [busy, setBusy]               = useState(false);
  const [err, setErr]                 = useState('');

  // Group state
  const [rollInput, setRollInput]     = useState('');
  const [inputMember, setInputMember] = useState(null);   // member found for current input
  const [inputErr, setInputErr]       = useState('');
  const [looking, setLooking]         = useState(false);
  const [members, setMembers]         = useState([]);      // confirmed list
  const [selfLoading, setSelfLoading] = useState(false);  // loading own entry
  const inputRef                      = useRef(null);
  const lookupTimer                   = useRef(null);

  // Live lookup as user types roll number
  useEffect(() => {
    const val = rollInput.trim();
    setInputMember(null);
    setInputErr('');
    if (!val) return;

    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      setLooking(true);
      try {
        const res = await invoiceAPI.lookupMembers([val]);
        if (res.found?.length) {
          setInputMember(res.found[0]);
          setInputErr('');
        } else {
          setInputErr(`Roll number "${val}" not found.`);
        }
      } catch (e) { setInputErr(e?.response?.data?.error || e?.message || 'Lookup failed.'); }
      finally { setLooking(false); }
    }, 500);
  }, [rollInput]);

  function addMember() {
    if (!inputMember) return;
    if (members.find((m) => m.roll_no === inputMember.roll_no)) {
      setInputErr('Member already added.');
      return;
    }
    setMembers((prev) => [...prev, inputMember]);
    setRollInput('');
    setInputMember(null);
    setInputErr('');
    inputRef.current?.focus();
  }

  function removeMember(rollNo) {
    setMembers((prev) => prev.filter((m) => m.roll_no !== rollNo));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && inputMember) { e.preventDefault(); addMember(); }
  }

  const groupTotal = members.reduce((s, m) => s + Number(m.fee), 0);

  // Enter group step: pre-add self as first member
  async function enterGroupStep() {
    setErr('');
    setMembers([]);
    setStep('group-input');
    if (!myMember?.roll_no) return;
    setSelfLoading(true);
    try {
      const res = await invoiceAPI.lookupMembers([String(myMember.roll_no)]);
      if (res.found?.length) {
        setMembers([res.found[0]]);
      }
    } catch { /* silently skip — user can add themselves manually */ }
    finally { setSelfLoading(false); }
  }

  // Individual: generate immediately
  async function handleIndividual() {
    setBusy(true); setErr('');
    try {
      const res = await invoiceAPI.generate('individual');
      setResult(res);
      setStep('done');
      onDone?.();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to generate invoice.');
    } finally { setBusy(false); }
  }

  // Group: generate all + download PDF
  async function handleGenerateGroup() {
    if (!members.length) { setErr('Add at least one member.'); return; }
    setBusy(true); setErr('');
    try {
      const rolls = members.map((m) => m.roll_no);
      const res   = await invoiceAPI.generate('group', rolls);
      setResult(res);
      const ids       = res.invoices.map((inv) => inv.id);
      const year      = res.invoices[0]?.fee_year ?? new Date().getFullYear();
      const refNo     = res.reference_no ?? '';
      await invoiceAPI.openGroupPdf(ids, year, refNo);
      setStep('done');
      onDone?.();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to generate invoice.');
    } finally { setBusy(false); }
  }

  async function handleDownloadIndividual() {
    if (!result?.invoices?.[0]) return;
    setBusy(true);
    try { await invoiceAPI.openPdf(result.invoices[0].id); }
    catch (e) { setErr(e.message || 'Download failed.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-[28px] bg-white shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-800">Generate Invoice</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-5">
          {err && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>
          )}

          {/* ── Step: choose ── */}
          {step === 'choose' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Select invoice type:</p>
              <button
                onClick={handleIndividual}
                disabled={busy}
                className="flex w-full items-center gap-4 rounded-[22px] border-2 border-blue-200 bg-blue-50 p-4 text-left transition hover:border-blue-400 hover:bg-blue-100 disabled:opacity-60"
              >
                <span className="text-3xl">👤</span>
                <div>
                  <p className="font-bold text-slate-800">Individual</p>
                  <p className="text-xs text-slate-500">Generate an invoice for yourself only</p>
                </div>
                {busy && <span className="ml-auto text-xs text-slate-400">Generating…</span>}
              </button>
              <button
                onClick={enterGroupStep}
                className="flex w-full items-center gap-4 rounded-[22px] border-2 border-violet-200 bg-violet-50 p-4 text-left transition hover:border-violet-400 hover:bg-violet-100"
              >
                <span className="text-3xl">👥</span>
                <div>
                  <p className="font-bold text-slate-800">Group</p>
                  <p className="text-xs text-slate-500">One combined invoice for multiple members</p>
                </div>
              </button>
            </div>
          )}

          {/* ── Step: group-input ── */}
          {step === 'group-input' && (
            <div className="space-y-4">
              {/* Roll number input */}
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Add Member by Roll Number
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      ref={inputRef}
                      type="text"
                      value={rollInput}
                      onChange={(e) => setRollInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="e.g. 1081"
                      autoFocus
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 font-mono text-sm text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                    {/* Inline name preview */}
                    {looking && (
                      <p className="mt-1.5 px-1 text-xs text-slate-400">Searching…</p>
                    )}
                    {!looking && inputMember && (
                      <p className="mt-1.5 px-1 text-xs font-semibold text-emerald-600">
                        ✓ {inputMember.full_name}
                      </p>
                    )}
                    {!looking && inputErr && rollInput.trim() && (
                      <p className="mt-1.5 px-1 text-xs text-red-500">{inputErr}</p>
                    )}
                  </div>
                  <button
                    onClick={addMember}
                    disabled={!inputMember}
                    className="rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Member list */}
              {(selfLoading || members.length > 0) && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                    {selfLoading ? 'Loading…' : `Members (${members.length})`}
                  </p>
                  {!selfLoading && (
                    <div className="max-h-52 overflow-y-auto rounded-2xl border border-slate-200">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-slate-500">Roll No</th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-500">Name</th>
                            <th className="px-3 py-2 text-right font-semibold text-slate-500">Amount</th>
                            <th className="px-3 py-2" />
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((m, i) => (
                            <tr key={m.roll_no} className={`border-t border-slate-100 ${i === 0 ? 'bg-blue-50' : ''}`}>
                              <td className="px-3 py-2 font-mono text-slate-700">{m.roll_no}</td>
                              <td className="px-3 py-2 text-slate-700">
                                {m.full_name}
                                {i === 0 && <span className="ml-1.5 text-xs text-blue-500">(you)</span>}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-700">
                                {Number(m.fee).toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {i !== 0 && (
                                  <button
                                    onClick={() => removeMember(m.roll_no)}
                                    className="rounded-xl bg-red-50 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-100"
                                  >
                                    Remove
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Total */}
                  {!selfLoading && (
                    <div className="mt-2 flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-2.5">
                      <span className="text-sm font-semibold text-slate-600">Total (TZS)</span>
                      <span className="text-base font-bold text-slate-900">{groupTotal.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { setErr(''); setMembers([]); setRollInput(''); setInputMember(null); setStep('choose'); }}
                  className="flex-1 rounded-2xl bg-slate-100 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                >
                  Back
                </button>
                <button
                  onClick={handleGenerateGroup}
                  disabled={busy || !members.length}
                  className="flex-1 rounded-2xl bg-violet-600 py-3 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  {busy ? 'Generating PDF…' : `Generate Invoice (${members.length})`}
                </button>
              </div>
            </div>
          )}

          {/* ── Step: done ── */}
          {step === 'done' && result && (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-3xl">
                ✅
              </div>
              {result.type === 'individual' ? (
                <>
                  <p className="font-semibold text-slate-800">Invoice generated!</p>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700 text-left space-y-1">
                    <p><span className="font-semibold">Reference:</span> {result.invoices[0]?.reference_no}</p>
                    <p><span className="font-semibold">Year:</span> {result.invoices[0]?.fee_year}</p>
                    <p><span className="font-semibold">Amount:</span> TZS {Number(result.invoices[0]?.fee || 0).toLocaleString()}</p>
                  </div>
                  <button
                    onClick={handleDownloadIndividual}
                    disabled={busy}
                    className="w-full rounded-2xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {busy ? 'Downloading…' : 'Download PDF'}
                  </button>
                </>
              ) : (
                <>
                  <p className="font-semibold text-slate-800">Group Invoice Generated!</p>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700 text-left space-y-1">
                    <p><span className="font-semibold">Reference No:</span>{' '}
                      <span className="font-mono font-bold text-blue-700">{result.reference_no}</span>
                    </p>
                    <p><span className="font-semibold">Members:</span> {result.invoices.length}</p>
                    <p><span className="font-semibold">Total:</span> TZS {Number(result.total).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-slate-500">PDF downloaded. Pay at CRDB using the Control No above.</p>
                </>
              )}
              <button
                onClick={onClose}
                className="w-full rounded-2xl bg-slate-100 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const CALENDAR_YEAR = new Date().getFullYear();

export default function MemberPayments() {
  const [allInvoices, setAllInvoices] = useState([]);   // all records, unfiltered
  const [myMember, setMyMember]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [status, setStatus]         = useState('');
  // The year currently payable/visible to members — normally the calendar year, but
  // advances to next year once an admin opens it early via Fee Settings
  // (system_settings.next_year_fee_open_date, surfaced here as current_fee_year).
  const [thisYear, setThisYear]     = useState(CALENDAR_YEAR);
  const THIS_YEAR = thisYear;
  const [selectedYear, setSelectedYear] = useState(CALENDAR_YEAR);
  const yearTouchedRef = useRef(false);
  const [error, setError]           = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal]   = useState(false);
  const [downloading, setDownloading]   = useState(false);
  const [lifeBrackets, setLifeBrackets] = useState([]);
  const [payMethods, setPayMethods]     = useState([]);
  const [outstanding, setOutstanding]   = useState(null);
  const [depositSlipEnabled, setDepositSlipEnabled] = useState(true);
  const [mySlips, setMySlips] = useState([]);

  // All unique years available in invoices (always include current year)
  const availableYears = [...new Set([THIS_YEAR, ...allInvoices.map((p) => Number(p.fee_year))])]
    .sort((a, b) => b - a);

  // Invoices filtered by selected year + status
  // Exclude ALL current-year AWTF invoices from bottom grid — shown in top CurrentYearCard
  // Exclude arrears invoices from ALL years — shown in ArrearsSection
  const payments = allInvoices.filter((p) => {
    if (Number(p.fee_year) !== selectedYear) return false;
    if (p.invoice_group && String(p.invoice_group).startsWith('arrears-')) return false;
    if (selectedYear === THIS_YEAR && (p.fee_type || '').toUpperCase() === 'AWTF') return false;
    if (status === 'PAID') return isPaid(p);
    if (status === 'PENDING') return !isPaid(p);
    return true;
  });

  // Summary for selected year
  const yearInvoices = allInvoices.filter((p) => Number(p.fee_year) === selectedYear);
  // If no invoice has been generated yet for this year, fall back to the amount owed
  // per the outstanding/arrears calculation instead of showing TZS 0.
  const outstandingYearEntry = outstanding?.years?.find((y) => Number(y.year) === selectedYear);
  const summary = {
    total_paid:        yearInvoices.filter(isPaid).reduce((s, p) => s + Number(p.fee || 0), 0),
    total_outstanding: yearInvoices.length > 0
      ? yearInvoices.filter((p) => !isPaid(p)).reduce((s, p) => s + Number(p.fee || 0), 0)
      : Number(outstandingYearEntry?.amount || 0),
  };

  const totalPages = Math.max(1, Math.ceil(payments.length / ITEMS_PER_PAGE));
  const paginatedPayments = payments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const fetchPayments = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [member, lifeRes, pmRes, feeCfgRes] = await Promise.all([
        membersAPI.me(),
        feeConfigAPI.getLifeMembershipFees().catch(() => ({ fees: [] })),
        paymentMethodsAPI.list().catch(() => ({ methods: [] })),
        feeConfigAPI.getAll().catch(() => ({ current_fee_year: CALENDAR_YEAR })),
      ]);
      setPayMethods(pmRes.methods || []);
      setMyMember(member);
      setLifeBrackets(lifeRes.fees || []);
      const newThisYear = Number(feeCfgRes.current_fee_year) || CALENDAR_YEAR;
      setThisYear(newThisYear);
      if (!yearTouchedRef.current) setSelectedYear(newThisYear);
      const [resp, debtRes] = await Promise.all([
        paymentsAPI.getByMember(member.id),
        membersAPI.outstanding(member.id).catch(() => null),
      ]);
      setAllInvoices(resp.invoices || []);
      setOutstanding(debtRes);
      settingsAPI.getDepositSlipStatus()
        .then((r) => setDepositSlipEnabled(r.enabled !== false))
        .catch(() => setDepositSlipEnabled(true));
      paymentSlipsAPI.getByMember(member.id)
        .then((r) => setMySlips(r.slips || []))
        .catch(() => setMySlips([]));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load payments.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);
  useEffect(() => { setCurrentPage(1); }, [status, selectedYear]);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);

  // Current-year AWTF invoice (regular or arrears) — shown in the top card
  // Arrears-tagged invoices (invoice_group starting with "arrears-") are shown
  // exclusively in the Arrears section below, even if their fee_year is the
  // current year — otherwise the same invoice would appear twice.
  const thisYearAwtfInvoice = useMemo(() =>
    allInvoices
      .filter(p => Number(p.fee_year) === THIS_YEAR
        && (p.fee_type || '').toUpperCase() === 'AWTF'
        && !String(p.invoice_group || '').startsWith('arrears-'))
      .sort((a, b) => Number(b.id) - Number(a.id))[0] ?? null,
  [allInvoices]);

  const hasAnyThisYear = allInvoices.some(p => Number(p.fee_year) === THIS_YEAR);
  // When there's outstanding debt, the Arrears section already offers the "generate"
  // action for the current year — avoid showing the same prompt twice.
  const currentYearCoveredByArrears = !!(outstanding && !outstanding.exempt && outstanding.total_debt > 0);

  // Once an arrears invoice is paid, outstanding.total_debt drops to 0 — but we still
  // want to show the Arrears card (now in its "Paid" state) instead of it just vanishing.
  const hasArrearsInvoice = !!myMember?.id
    && allInvoices.some(p => p.invoice_group === 'arrears-' + myMember.id);

  const yearIdx = availableYears.indexOf(selectedYear);

  return (
    <div className="space-y-4">
      {showModal && (
        <GenerateModal
          onClose={() => setShowModal(false)}
          onDone={fetchPayments}
          myMember={myMember}
        />
      )}

      {/* ── Hero header ── */}
      <div className="overflow-hidden rounded-[26px] bg-[linear-gradient(135deg,#0f172a_0%,#0e4f6b_60%,#f07415_100%)] px-6 py-5 text-white shadow-lg">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-orange-200/70">AWTF Welfare Fund</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight">My Payments</h1>
        {/* Year nav + filter */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2 py-1 backdrop-blur-sm">
            <button
              onClick={() => { if (yearIdx < availableYears.length - 1) { yearTouchedRef.current = true; setSelectedYear(availableYears[yearIdx + 1]); } }}
              disabled={yearIdx >= availableYears.length - 1}
              className="flex h-6 w-6 items-center justify-center rounded-full text-white/70 hover:bg-white/20 disabled:opacity-30 font-bold">
              ‹
            </button>
            <span className="min-w-[46px] text-center text-sm font-bold text-white">{selectedYear}</span>
            <button
              onClick={() => { if (yearIdx > 0) { yearTouchedRef.current = true; setSelectedYear(availableYears[yearIdx - 1]); } }}
              disabled={yearIdx <= 0}
              className="flex h-6 w-6 items-center justify-center rounded-full text-white/70 hover:bg-white/20 disabled:opacity-30 font-bold">
              ›
            </button>
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm focus:outline-none">
            <option value="" className="text-slate-800">All</option>
            <option value="PENDING" className="text-slate-800">Pending</option>
            <option value="PAID" className="text-slate-800">Paid</option>
          </select>
        </div>
      </div>

      {/* ── Senior Exemption Banner ── */}
      {!loading && myMember?.is_senior_exempt && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 flex gap-3 items-start">
          <span className="shrink-0 text-xl mt-0.5">🎖</span>
          <div>
            <p className="font-bold text-emerald-800 text-sm">Annual Fee Exemption — Age 66+</p>
            <p className="mt-0.5 text-xs text-emerald-700 leading-relaxed">
              You are exempted from annual contributions effective 1 July 2026 (AGM Resolution No. 5, May 2026). You retain full benefits provided you contributed for at least 10 years prior to age 66.
            </p>
          </div>
        </div>
      )}

      {/* ── Loading / error ── */}
      {loading && (
        <div className="flex items-center gap-3 text-slate-400 py-12 justify-center text-sm">
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          Loading payments…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">{error}</div>
      )}

      {/* ── Annual fees (left) + Life Membership (right) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      <div className="space-y-4 min-w-0">

      {/* ── Top grid: stats + current year invoice ── */}
      {!loading && !error && (() => {
        // Hidden when no invoice exists yet and the Arrears section below
        // already offers the same "generate" action for the current year.
        const showCurrentYearCard = thisYearAwtfInvoice || !currentYearCoveredByArrears;

        const miniStats = (
          <>
            <div className="flex-1 rounded-2xl border border-emerald-200/70 bg-white px-4 py-3.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Paid · {selectedYear}</p>
              </div>
              <p className="text-lg font-extrabold text-slate-900">
                <span className="text-[10px] font-semibold text-slate-400 mr-1">TZS</span>
                {yearInvoices.filter(isPaid).reduce((s, p) => s + Number(p.fee || 0), 0).toLocaleString()}
              </p>
            </div>
            <div className="flex-1 rounded-2xl border border-amber-200/70 bg-white px-4 py-3.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Outstanding · {selectedYear}</p>
              </div>
              <p className="text-lg font-extrabold text-slate-900">
                <span className="text-[10px] font-semibold text-slate-400 mr-1">TZS</span>
                {summary.total_outstanding.toLocaleString()}
              </p>
            </div>
          </>
        );

        return showCurrentYearCard ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_2fr]">
            <div className="flex flex-row sm:flex-col gap-3">{miniStats}</div>
            <CurrentYearCard
              invoice={thisYearAwtfInvoice}
              year={THIS_YEAR}
              payMethods={payMethods}
              onGenerate={() => setShowModal(true)}
              showGenerate={!hasAnyThisYear && !myMember?.is_senior_exempt}
              onRefresh={fetchPayments}
              depositSlipEnabled={depositSlipEnabled}
              mySlips={mySlips}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">{miniStats}</div>
        );
      })()}

      {/* ── Annual Fees Arrears ── */}
      {!loading && outstanding && !outstanding.exempt && (outstanding.total_debt > 0 || hasArrearsInvoice) && (
        <ArrearsSection
          data={outstanding}
          allInvoices={allInvoices}
          memberId={myMember?.id}
          onRefresh={fetchPayments}
          payMethods={payMethods}
          depositSlipEnabled={depositSlipEnabled}
          mySlips={mySlips}
        />
      )}

      </div>

      {/* ── Life Membership Card ── */}
      <div className="min-w-0">
      {!loading && myMember && (
        <LifeMembershipCard
          member={myMember}
          brackets={lifeBrackets}
          onRefresh={fetchPayments}
          lifeInvoice={allInvoices.find(p => p.fee_type === 'LIFE') ?? null}
        />
      )}
      </div>

      </div>

      {/* ── Invoice 2-col grid ── */}
      {!loading && !error && (
        payments.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paginatedPayments.map(p => (
                <InvoiceCard key={p.id} payment={p} payMethods={payMethods} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-slate-400">Page {currentPage} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="rounded-2xl border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 shadow-sm">
                    ← Previous
                  </button>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-2xl bg-slate-800 px-5 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-40 shadow-sm">
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        ) : selectedYear === THIS_YEAR && (hasAnyThisYear || currentYearCoveredByArrears) ? (
          // Current year's invoice/debt is already shown above (CurrentYearCard or
          // Arrears section) — nothing else to say here.
          null
        ) : (
          <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl">📄</div>
            <p className="font-semibold text-slate-700">No invoices for {selectedYear}</p>
            <p className="mt-1 text-sm text-slate-400">
              {selectedYear === THIS_YEAR && !myMember?.is_senior_exempt
                ? 'Generate your annual invoice to get started.'
                : 'No records found for this year.'}
            </p>
            {selectedYear === THIS_YEAR && !myMember?.is_senior_exempt && (
              <button onClick={() => setShowModal(true)}
                className="mt-5 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700">
                + Generate Invoice
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
}

function PdfPreviewModal({ url, title, filename, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  function handleDownload() {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-white px-4 py-3 shadow">
        <p className="truncate text-sm font-semibold text-slate-800">{title}</p>
        <div className="flex shrink-0 items-center gap-2">
          <button onClick={handleDownload}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">
            ⬇ Download
          </button>
          <button onClick={onClose}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            ✕ Close
          </button>
        </div>
      </div>
      {/* PDF frame */}
      <iframe src={url} title={title} className="min-h-0 flex-1 w-full bg-slate-200" />
    </div>,
    document.body
  );
}


function HowToPayModal({ methods, onClose }) {
  const [active, setActive] = useState(methods[0]?.slug ?? '');
  const activeMethod = methods.find(m => m.slug === active) ?? methods[0];
  const info = { dial: activeMethod?.dial ?? '', steps: activeMethod?.steps ?? [] };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
      onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bg-slate-800 px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Payment</p>
            <p className="text-base font-bold text-white mt-0.5">How to Pay</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none">&times;</button>
        </div>

        {/* Method tabs — logos */}
        <div className="px-4 pt-4 pb-3 border-b border-slate-100">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2.5">
            Select payment method
          </p>
          <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${methods.length}, 1fr)` }}>
            {methods.map(m => (
              <button key={m.slug} onClick={() => setActive(m.slug)}
                className={`rounded-xl p-2 flex flex-col items-center justify-center min-h-[56px] transition-all border-2 ${
                  active === m.slug
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-slate-100 bg-slate-50 hover:border-slate-200'
                }`}>
                {m.logo_url
                  ? <img src={m.logo_url} alt={m.name} className="max-h-8 max-w-full object-contain" />
                  : <span className="text-[9px] font-bold text-slate-600 text-center leading-tight px-1">{m.name}</span>
                }
              </button>
            ))}
          </div>
        </div>

        {/* Steps for active method */}
        <div className="p-5 space-y-4">
          {info.dial && (
            <div className="rounded-xl bg-slate-800 px-4 py-2.5 flex items-center justify-between">
              <span className="text-xs text-white/50">Dial / Platform</span>
              <span className="font-mono font-bold text-white tracking-widest">{info.dial}</span>
            </div>
          )}
          <ol className="space-y-3">
            {info.steps.map((step, i) => (
              <li key={i} className="flex gap-3 items-start">
                <span className="shrink-0 h-5 w-5 rounded-full bg-slate-700 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-700">{step}</span>
              </li>
            ))}
          </ol>
          <p className="text-[11px] text-slate-400 border-t border-slate-100 pt-3 leading-relaxed">
            After payment, your invoice status will change to <strong className="text-emerald-600">Paid</strong> within a few minutes.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

function GroupMembersModal({ referenceNo, onClose }) {
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState('');

  useEffect(() => {
    paymentsAPI.getGroup(referenceNo)
      .then(setData)
      .catch(() => setErr('Failed to load group members.'));
  }, [referenceNo]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-violet-700 px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Group Invoice</p>
            <p className="text-base font-bold text-white">{referenceNo}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none">&times;</button>
        </div>
        <div className="p-5">
          {err && <p className="text-red-600 text-sm">{err}</p>}
          {!data && !err && <p className="text-slate-500 text-sm">Loading…</p>}
          {data && (
            <>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="pb-2 text-left text-xs font-semibold text-slate-400 uppercase">Member</th>
                    <th className="pb-2 text-right text-xs font-semibold text-slate-400 uppercase">Amount</th>
                    <th className="pb-2 text-right text-xs font-semibold text-slate-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.members.map(m => (
                    <tr key={m.id}>
                      <td className="py-2.5 pr-3">
                        <p className="font-semibold text-slate-800">{m.full_name}</p>
                        <p className="text-xs text-slate-500 font-mono">{m.roll_no}</p>
                      </td>
                      <td className="py-2.5 text-right font-semibold text-slate-700">
                        {Number(m.fee).toLocaleString()}
                      </td>
                      <td className="py-2.5 text-right">
                        <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${
                          String(m.paystatus).toUpperCase() === 'PAID'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>{m.paystatus}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Total</span>
                <span className="text-lg font-black text-violet-800">TZS {Number(data.total).toLocaleString()}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function InvoiceCard({ payment: p, payMethods = [] }) {
  const [busy, setBusy]           = useState('');
  const [preview, setPreview]     = useState(null);
  const [showGroup, setShowGroup] = useState(false);
  const [showHTP, setShowHTP]     = useState(false);
  const paid    = isPaid(p);
  const isLife  = p.fee_type === 'LIFE';
  const isGroup = Number(p.group_count) > 1;
  const amount  = isGroup
    ? Number(p.group_total || 0).toLocaleString()
    : Number(p.fee || 0).toLocaleString();

  const headerBg  = isLife ? (paid ? 'bg-violet-700' : 'bg-violet-600') : (paid ? 'bg-emerald-600' : 'bg-slate-700');
  const ringColor = isLife ? (paid ? 'ring-violet-300' : 'ring-violet-200') : (paid ? 'ring-emerald-200' : 'ring-slate-200');
  const accentCls = isLife ? 'text-violet-600' : 'text-blue-600';
  const accentBg  = isLife ? 'bg-violet-50' : 'bg-blue-50';
  const accentBullet = isLife ? 'bg-violet-600' : 'bg-blue-600';
  const accentText   = isLife ? 'text-violet-700' : 'text-blue-700';
  const accentBold   = isLife ? 'text-violet-900' : 'text-blue-900';
  const invoiceBtnCls = paid
    ? 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
    : (isLife ? 'bg-violet-600 text-white hover:bg-violet-700' : 'bg-blue-600 text-white hover:bg-blue-700');

  async function openPreview(type) {
    setBusy(type);
    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'invoice' ? `/api/invoices/${p.id}/pdf` : `/api/invoices/${p.id}/receipt`;
      const r = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const label = type === 'invoice' ? 'Invoice' : 'Receipt';
      setPreview({ url, title: `${label} — ${p.reference_no}`, filename: `AWTF-${label}-${p.id}.pdf` });
    } catch (e) { console.error(e); }
    finally { setBusy(''); }
  }

  function closePreview() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  return (
    <>
      {preview && <PdfPreviewModal {...preview} onClose={closePreview} />}
      {showGroup && <GroupMembersModal referenceNo={p.reference_no} onClose={() => setShowGroup(false)} />}
      {showHTP && payMethods.length > 0 && <HowToPayModal methods={payMethods} onClose={() => setShowHTP(false)} />}

      <div className={`rounded-2xl bg-white shadow-sm ring-1 overflow-hidden ${ringColor}`}>

        {/* Header */}
        <div className={`px-5 py-4 ${headerBg}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">
                {isLife ? 'Life Membership' : 'AWTF Welfare Fund'}
              </p>
              <p className="mt-0.5 text-base font-bold text-white">
                {isLife ? 'One-Time Lump Sum' : `Year ${p.fee_year}`}
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-bold tracking-wide
              ${paid ? (isLife ? 'bg-white text-violet-700' : 'bg-white text-emerald-700') : 'bg-amber-400 text-amber-900'}`}>
              {paid ? '✓ Paid' : 'Pending'}
            </span>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm font-semibold text-white/60">TZS</span>
              <span className="text-3xl font-black text-white">{amount}</span>
              {isGroup && (
                <span className="text-xs text-white/60 ml-1">
                  (your share: {Number(p.fee).toLocaleString()})
                </span>
              )}
            </div>
            {isGroup && (
              <button
                onClick={() => setShowGroup(true)}
                title="View group members"
                className="rounded-full bg-white/20 hover:bg-white/35 p-1.5 transition-colors shrink-0">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M17 20h5v-2a4 4 0 0 0-3-3.87M9 20H4v-2a4 4 0 0 1 3-3.87m6 5.87v-2a4 4 0 0 0-2.5-3.7M15 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm6 4a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM3 11a3 3 0 1 1 6 0 3 3 0 0 1-6 0Z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Payment Reference</p>
            <p className="mt-1 font-mono text-xl font-bold tracking-wider text-slate-900">{p.reference_no}</p>
          </div>

          <div className="flex gap-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Due Date</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-700">{p.duedate || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Fee Type</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-700">{isLife ? 'Life Membership' : 'AWTF Welfare'}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2">
            {paid && (
              <button onClick={() => openPreview('receipt')} disabled={busy !== ''}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                🧾 {busy === 'receipt' ? 'Loading…' : 'View Receipt'}
              </button>
            )}
            <button onClick={() => openPreview('invoice')} disabled={busy !== ''}
              className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold disabled:opacity-60 ${invoiceBtnCls}`}>
              📄 {busy === 'invoice' ? 'Loading…' : 'View Invoice'}
            </button>
            {!paid && payMethods.length > 0 && (
              <button onClick={() => setShowHTP(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50 py-3 text-sm font-semibold text-slate-600 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
                </svg>
                How to Pay
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}


// ─── Life Membership Invoice Modal ──────────────────────────────────────────

function LifeInvoiceModal({ myMember, brackets, onClose, onDone }) {
  const [step, setStep]           = useState('choose'); // choose | group-input | done
  const [result, setResult]       = useState(null);
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState('');

  // Group state
  const [rollInput, setRollInput]     = useState('');
  const [inputMember, setInputMember] = useState(null);
  const [inputErr, setInputErr]       = useState('');
  const [looking, setLooking]         = useState(false);
  const [members, setMembers]         = useState([]);
  const [selfLoading, setSelfLoading] = useState(false);
  const inputRef                      = useRef(null);
  const lookupTimer                   = useRef(null);

  const myAge     = useMemo(() => calcAge(myMember?.date_of_birth), [myMember]);
  const myBracket = useMemo(() => findBracket(brackets, myAge), [brackets, myAge]);

  // Live lookup for group input
  useEffect(() => {
    const val = rollInput.trim();
    setInputMember(null); setInputErr('');
    if (!val) return;
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      setLooking(true);
      try {
        const res = await invoiceAPI.lookupMembersLife([val]);
        const found = res.found?.[0];
        if (!found) { setInputErr(`Roll number "${val}" not found.`); return; }
        if (found.is_life) { setInputErr(`${found.full_name} is already a life member.`); return; }
        if (!found.fee) { setInputErr(found.error || 'No applicable bracket.'); return; }
        setInputMember(found);
      } catch { setInputErr('Lookup failed.'); }
      finally { setLooking(false); }
    }, 500);
  }, [rollInput]);

  function addMember() {
    if (!inputMember) return;
    if (members.find(m => m.roll_no === inputMember.roll_no)) {
      setInputErr('Already added.'); return;
    }
    setMembers(prev => [...prev, inputMember]);
    setRollInput(''); setInputMember(null); setInputErr('');
    inputRef.current?.focus();
  }

  async function enterGroup() {
    setErr(''); setMembers([]); setStep('group-input');
    if (!myMember?.roll_no) return;
    setSelfLoading(true);
    try {
      const res = await invoiceAPI.lookupMembersLife([String(myMember.roll_no)]);
      const found = res.found?.[0];
      if (found && found.fee && !found.is_life) setMembers([found]);
    } catch { /* skip */ }
    finally { setSelfLoading(false); }
  }

  async function handleIndividual() {
    setBusy(true); setErr('');
    try {
      const res = await invoiceAPI.generateLife('individual');
      setResult(res); setStep('done'); onDone?.();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed.');
    } finally { setBusy(false); }
  }

  async function handleGroup() {
    if (!members.length) { setErr('Add at least one member.'); return; }
    setBusy(true); setErr('');
    try {
      const rolls = members.map(m => m.roll_no);
      const res   = await invoiceAPI.generateLife('group', rolls);
      setResult(res);
      // Download combined PDF
      const ids   = res.invoices.map(inv => inv.id);
      const refNo = res.reference_no ?? '';
      await invoiceAPI.openGroupPdf(ids, new Date().getFullYear(), refNo);
      setStep('done'); onDone?.();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed.');
    } finally { setBusy(false); }
  }

  async function handleDownloadIndividual() {
    if (!result?.invoices?.[0]) return;
    setBusy(true);
    try { await invoiceAPI.openPdf(result.invoices[0].id); }
    catch (e) { setErr(e.message || 'Download failed.'); }
    finally { setBusy(false); }
  }

  const groupTotal = members.reduce((s, m) => s + Number(m.fee || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-[28px] bg-white shadow-2xl">

        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Life Membership Invoice</h2>
            <p className="text-xs text-violet-600 font-medium mt-0.5">One-time lump sum payment</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-5">
          {err && <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>}

          {/* Step: choose */}
          {step === 'choose' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Select invoice type:</p>

              {/* Individual */}
              {myBracket ? (
                <button onClick={handleIndividual} disabled={busy}
                  className="flex w-full items-center gap-4 rounded-[22px] border-2 border-violet-200 bg-violet-50 p-4 text-left transition hover:border-violet-400 hover:bg-violet-100 disabled:opacity-60">
                  <span className="text-3xl">👤</span>
                  <div className="flex-1">
                    <p className="font-bold text-slate-800">Individual</p>
                    <p className="text-xs text-slate-500">Invoice for yourself only</p>
                    <p className="text-sm font-bold text-violet-700 mt-1">
                      TZS {Number(myBracket.amount).toLocaleString()}
                      <span className="text-xs font-normal text-slate-400 ml-1">(age {myAge}, bracket {myBracket.age_from}–{myBracket.age_to})</span>
                    </p>
                  </div>
                  {busy && <span className="ml-auto text-xs text-slate-400">Generating…</span>}
                </button>
              ) : (
                <div className="rounded-[22px] border-2 border-slate-200 bg-slate-50 p-4 opacity-60">
                  <p className="font-bold text-slate-500">Individual — Not Available</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {myAge == null ? 'Date of birth not set in your profile.' : `Age ${myAge} is not covered by any life membership bracket (20–65).`}
                  </p>
                </div>
              )}

              {/* Group */}
              <button onClick={enterGroup}
                className="flex w-full items-center gap-4 rounded-[22px] border-2 border-blue-200 bg-blue-50 p-4 text-left transition hover:border-blue-400 hover:bg-blue-100">
                <span className="text-3xl">👥</span>
                <div>
                  <p className="font-bold text-slate-800">Group</p>
                  <p className="text-xs text-slate-500">One combined invoice for multiple members (each pays their own bracket amount)</p>
                </div>
              </button>
            </div>
          )}

          {/* Step: group-input */}
          {step === 'group-input' && (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Add Member by Roll Number</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input ref={inputRef} type="text" value={rollInput}
                      onChange={e => setRollInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && inputMember) { e.preventDefault(); addMember(); } }}
                      placeholder="e.g. 1081" autoFocus
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 font-mono text-sm text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
                    {looking && <p className="mt-1.5 px-1 text-xs text-slate-400">Searching…</p>}
                    {!looking && inputMember && (
                      <p className="mt-1.5 px-1 text-xs font-semibold text-emerald-600">
                        ✓ {inputMember.full_name} — TZS {Number(inputMember.fee).toLocaleString()} (age {inputMember.age})
                      </p>
                    )}
                    {!looking && inputErr && rollInput.trim() && (
                      <p className="mt-1.5 px-1 text-xs text-red-500">{inputErr}</p>
                    )}
                  </div>
                  <button onClick={addMember} disabled={!inputMember}
                    className="rounded-2xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
                    Add
                  </button>
                </div>
              </div>

              {(selfLoading || members.length > 0) && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                    {selfLoading ? 'Loading…' : `Members (${members.length})`}
                  </p>
                  {!selfLoading && (
                    <div className="max-h-52 overflow-y-auto rounded-2xl border border-slate-200">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-slate-500">Roll No</th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-500">Name</th>
                            <th className="px-3 py-2 text-right font-semibold text-slate-500">Amount</th>
                            <th className="px-3 py-2" />
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((m, i) => (
                            <tr key={m.roll_no} className={`border-t border-slate-100 ${i === 0 ? 'bg-violet-50' : ''}`}>
                              <td className="px-3 py-2 font-mono text-slate-700">{m.roll_no}</td>
                              <td className="px-3 py-2 text-slate-700">
                                {m.full_name}
                                {i === 0 && <span className="ml-1.5 text-xs text-violet-500">(you)</span>}
                                <span className="ml-1 text-[10px] text-slate-400">age {m.age}</span>
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-violet-700">{Number(m.fee).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right">
                                {i !== 0 && (
                                  <button onClick={() => setMembers(prev => prev.filter(x => x.roll_no !== m.roll_no))}
                                    className="rounded-xl bg-red-50 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-100">
                                    Remove
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!selfLoading && (
                    <div className="mt-2 flex items-center justify-between rounded-2xl bg-violet-50 px-4 py-2.5">
                      <span className="text-sm font-semibold text-violet-700">Total (TZS)</span>
                      <span className="text-base font-bold text-violet-900">{groupTotal.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button onClick={() => { setErr(''); setMembers([]); setRollInput(''); setInputMember(null); setStep('choose'); }}
                  className="flex-1 rounded-2xl bg-slate-100 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200">
                  Back
                </button>
                <button onClick={handleGroup} disabled={busy || !members.length}
                  className="flex-1 rounded-2xl bg-violet-600 py-3 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                  {busy ? 'Generating PDF…' : `Generate Invoice (${members.length})`}
                </button>
              </div>
            </div>
          )}

          {/* Step: done */}
          {step === 'done' && result && (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-violet-100 text-3xl">🏆</div>
              {result.type === 'life-individual' ? (
                <>
                  <p className="font-semibold text-slate-800">Life Membership Invoice Generated!</p>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700 text-left space-y-1">
                    <p><span className="font-semibold">Reference:</span> {result.invoices[0]?.reference_no}</p>
                    <p><span className="font-semibold">Amount:</span> TZS {Number(result.invoices[0]?.fee || 0).toLocaleString()}</p>
                  </div>
                  <div className="rounded-xl bg-violet-50 border border-violet-200 p-3 text-xs text-violet-700 text-left">
                    Pay at any <strong>CRDB Bank</strong> branch using the reference number above.
                    Notify AWTF office after payment to activate your life member status.
                  </div>
                  <button onClick={handleDownloadIndividual} disabled={busy}
                    className="w-full rounded-2xl bg-violet-600 py-3 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
                    {busy ? 'Downloading…' : 'Download Invoice PDF'}
                  </button>
                </>
              ) : (
                <>
                  <p className="font-semibold text-slate-800">Group Life Membership Invoice!</p>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700 text-left space-y-1">
                    <p><span className="font-semibold">Reference No:</span>{' '}
                      <span className="font-mono font-bold text-violet-700">{result.reference_no}</span></p>
                    <p><span className="font-semibold">Members:</span> {result.invoices.length}</p>
                    <p><span className="font-semibold">Total:</span> TZS {Number(result.total).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-slate-500">PDF downloaded. Pay at CRDB using the Reference No above.</p>
                </>
              )}
              <button onClick={onClose}
                className="w-full rounded-2xl bg-slate-100 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Life Membership Card ────────────────────────────────────────────────────

function LifeMembershipCard({ member, brackets, onRefresh, lifeInvoice }) {
  const [showModal, setShowModal] = useState(false);
  const [dlBusy, setDlBusy]       = useState(false);
  const [dlErr, setDlErr]         = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [topupBusy, setTopupBusy]     = useState(false);
  const [topupErr, setTopupErr]       = useState('');
  const age     = calcAge(member.date_of_birth);
  const bracket = findBracket(brackets, age);
  const isLife  = member.membership_type === 'life';
  const notYetAdmitted = !member.roll_no;

  useEffect(() => {
    // Eligibility (10-year contribution requirement) only matters for the 66+ senior exemption.
    if (!member?.id || isLife || notYetAdmitted || age == null || age < 66) return;
    membersAPI.lifeEligibility(member.id).then(setEligibility).catch(() => {});
  }, [member?.id, isLife, notYetAdmitted, age]);

  async function handleDownload() {
    if (!lifeInvoice?.id) return;
    setDlBusy(true); setDlErr('');
    try { await invoiceAPI.openPdf(lifeInvoice.id); }
    catch (e) { setDlErr(e.message || 'Download failed.'); }
    finally { setDlBusy(false); }
  }

  async function handleGenerateTopup() {
    setTopupBusy(true); setTopupErr('');
    try {
      const res = await invoiceAPI.generateLifeTopup();
      setEligibility(prev => ({
        ...prev,
        existing_topup_invoice: { reference_no: res.reference_no, total: res.total, ids: res.ids },
      }));
    } catch (e) {
      setTopupErr(e.response?.data?.error || 'Failed to generate top-up invoice.');
    } finally {
      setTopupBusy(false);
    }
  }

  async function handleDownloadTopup() {
    const invoice = eligibility?.existing_topup_invoice;
    if (!invoice?.ids?.length) return;
    setTopupBusy(true); setTopupErr('');
    try {
      await invoiceAPI.openGroupPdf(invoice.ids, new Date().getFullYear(), invoice.reference_no);
    } catch (e) {
      setTopupErr(e.message || 'Download failed.');
    } finally {
      setTopupBusy(false);
    }
  }

  // Already a life member — show status
  if (isLife) {
    return (
      <div className="mb-6 rounded-2xl border-2 border-violet-300 bg-violet-50 p-5 flex items-center gap-4">
        <div className="h-12 w-12 shrink-0 rounded-full bg-violet-600 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="h-6 w-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="text-base font-bold text-violet-900">Life Member</p>
          <p className="text-sm text-violet-700 mt-0.5">
            You are a life member{member.life_member_date ? ` since ${member.life_member_date}` : ''}.
            You are exempt from annual contributions.
          </p>
        </div>
      </div>
    );
  }

  // Over 65 — senior exemption. This free exemption is earned through tenure, so it
  // only applies once formally admitted (unlike the paid lump-sum option below, which
  // anyone age-eligible can pay for regardless of admission status).
  if (age != null && age >= 66) {
    if (notYetAdmitted) return null;
    if (eligibility?.shortfall > 0) {
      return (
        <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-white shadow-sm overflow-hidden">
          <div className="flex items-start gap-4 bg-amber-50 p-5">
            <div className="h-12 w-12 shrink-0 rounded-full bg-amber-500 flex items-center justify-center text-white text-xl font-bold">66+</div>
            <div>
              <p className="text-base font-bold text-amber-900">Senior Exemption — Contribution Years Not Yet Met</p>
              <p className="text-sm text-amber-700 mt-0.5">
                You've reached the age of 66, but the exemption requires at least {eligibility.required_years} years
                of AWTF contributions — you currently have {eligibility.contributed_years}.
              </p>
            </div>
          </div>
          <div className="p-5">
            <ShortfallTopupPanel
              eligibility={eligibility}
              busy={topupBusy}
              err={topupErr}
              onGenerate={handleGenerateTopup}
              onDownload={handleDownloadTopup}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-4">
        <div className="h-12 w-12 shrink-0 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl font-bold">66+</div>
        <div>
          <p className="text-base font-bold text-emerald-900">Senior Member Exemption</p>
          <p className="text-sm text-emerald-700 mt-0.5">
            Members who have reached the age of 66 are exempt from annual contributions
            (effective 1st July 2026, with at least 10 years of contributions).
            Contact the AWTF office to confirm your exemption status.
          </p>
        </div>
      </div>
    );
  }

  // No date of birth on record — prompt the member to add it so we can show their bracket
  if (!member.date_of_birth) {
    return (
      <div className="mb-6 rounded-2xl border border-violet-200 bg-violet-50 p-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="h-12 w-12 shrink-0 rounded-full bg-violet-100 flex items-center justify-center text-2xl">🎂</div>
        <div className="flex-1">
          <p className="text-base font-bold text-violet-900">Add Your Date of Birth</p>
          <p className="text-sm text-violet-700 mt-0.5">
            Add your date of birth in your profile to see the one-time Life Membership lump-sum payment amount for your age.
          </p>
        </div>
        <Link to="/profile"
          className="shrink-0 rounded-xl bg-violet-600 hover:bg-violet-700 text-white px-4 py-2.5 text-sm font-semibold text-center transition-colors">
          Update Profile
        </Link>
      </div>
    );
  }

  // No matching bracket for this age — nothing to show
  if (!bracket) return null;

  return (
    <>
      {showModal && (
        <LifeInvoiceModal
          myMember={member}
          brackets={brackets}
          onClose={() => setShowModal(false)}
          onDone={() => { setShowModal(false); onRefresh?.(); }}
        />
      )}

      <div className="mb-6 rounded-2xl border-2 border-violet-200 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-violet-800 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">One-Time Payment Option</p>
              <p className="text-lg font-bold text-white mt-0.5">Life Membership</p>
            </div>
            <span className="rounded-full bg-violet-500/40 border border-violet-300/50 px-3 py-1 text-xs font-semibold text-white">
              Age {bracket.age_from}–{bracket.age_to}
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-sm font-semibold text-white/60">TZS</span>
            <span className="text-3xl font-black text-white">{Number(bracket.amount).toLocaleString()}</span>
            <span className="text-sm text-white/60 ml-1">lump sum</span>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            As a member aged <strong>{age}</strong>, you qualify to pay a one-time lump sum of{' '}
            <strong className="text-violet-700">TZS {Number(bracket.amount).toLocaleString()}</strong> to
            become a <strong>Life Member</strong> — permanently exempt from annual contributions.
          </p>

          {/* Benefits */}
          <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-violet-600 mb-3">Life Membership Benefits</p>
            {[
              'No annual contribution fees — ever',
              'Full benefits coverage for you and your beneficiaries',
              'Approved at AGM — 1st May 2026',
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  className="h-4 w-4 shrink-0 mt-0.5 text-violet-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
                </svg>
                <p className="text-xs text-violet-800">{b}</p>
              </div>
            ))}
          </div>

          {/* Brackets table */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Age Group</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Lump Sum (TZS)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {brackets.map(b => {
                  const isMine = Number(b.id) === Number(bracket.id);
                  return (
                    <tr key={b.id} className={isMine ? 'bg-violet-50' : ''}>
                      <td className={`px-3 py-2.5 text-sm ${isMine ? 'font-bold text-violet-800' : 'font-medium text-slate-700'}`}>
                        {b.age_from}–{b.age_to} years
                        {isMine && <span className="ml-2 text-[10px] font-bold text-violet-600 uppercase tracking-wide">Your bracket</span>}
                      </td>
                      <td className={`px-3 py-2.5 text-right ${isMine ? 'font-bold text-violet-700' : 'font-semibold text-slate-700'}`}>
                        {Number(b.amount).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Action buttons — hidden once an invoice already exists for this member.
              Note: the 10-year contribution requirement only gates the free 66+ senior
              exemption (see above) — the paid lump-sum option here has no tenure requirement. */}
          {dlErr && <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{dlErr}</div>}
          {lifeInvoice ? (
            <div className="flex items-center gap-4 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-violet-400 mb-1">
                  {isPaid(lifeInvoice) ? 'Payment received — awaiting confirmation' : 'Reference No'}
                </p>
                <p className="font-mono text-lg font-black text-slate-900 tracking-wider leading-none">{lifeInvoice.reference_no}</p>
                <p className="text-xs text-slate-500 mt-1">TZS {Number(lifeInvoice.fee).toLocaleString()}</p>
              </div>
              {!isPaid(lifeInvoice) && (
                <button
                  onClick={handleDownload}
                  disabled={dlBusy}
                  className="shrink-0 rounded-2xl bg-violet-600 px-5 py-3 text-sm font-bold text-white hover:bg-violet-700 disabled:opacity-60 shadow-sm transition-colors"
                >
                  {dlBusy ? 'Loading…' : '⬇ Invoice'}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowModal(true)}
                className="flex-1 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white py-3 text-sm font-semibold transition-colors text-center">
                Generate Individual Invoice
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="flex-1 rounded-2xl border-2 border-violet-600 text-violet-700 hover:bg-violet-50 py-3 text-sm font-semibold transition-colors text-center">
                Generate Group Invoice
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ShortfallTopupPanel({ eligibility, busy, err, onGenerate, onDownload, intro }) {
  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3">
      {intro && <p className="text-sm font-bold text-amber-900">{intro}</p>}
      <p className="text-xs text-amber-700">
        Pay for the remaining {eligibility.shortfall} year{eligibility.shortfall > 1 ? 's' : ''} (TZS{' '}
        {Number(eligibility.topup?.total || 0).toLocaleString()}, based on each year's actual historical
        rate) to qualify.
      </p>
      {err && <p className="text-xs text-red-600">{err}</p>}
      {eligibility.existing_topup_invoice ? (
        <div className="flex items-center gap-4 rounded-xl border border-amber-200 bg-white px-4 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-500 mb-1">Reference No</p>
            <p className="font-mono text-lg font-black text-slate-900 tracking-wider leading-none">
              {eligibility.existing_topup_invoice.reference_no}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              TZS {Number(eligibility.existing_topup_invoice.total).toLocaleString()}
            </p>
          </div>
          <button
            onClick={onDownload}
            disabled={busy}
            className="shrink-0 rounded-2xl bg-amber-600 px-5 py-3 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-60 shadow-sm transition-colors"
          >
            {busy ? 'Loading…' : '⬇ Invoice'}
          </button>
        </div>
      ) : (
        <button
          onClick={onGenerate}
          disabled={busy}
          className="w-full rounded-2xl bg-amber-600 py-3 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-60 shadow-sm transition-colors"
        >
          {busy ? 'Generating…' : `+ Generate Top-up Invoice · TZS ${Number(eligibility.topup?.total || 0).toLocaleString()}`}
        </button>
      )}
    </div>
  );
}

function ArrearsSection({ data, allInvoices, memberId, onRefresh, payMethods = [], depositSlipEnabled = true, mySlips = [] }) {
  const { fees_year, total_debt, years } = data;
  const [busy, setBusy]     = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [err, setErr]       = useState('');
  const [open, setOpen]     = useState(false);
  const [showSlip, setShowSlip] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [showHTP, setShowHTP] = useState(false);

  const existingInvoice = useMemo(() => {
    if (!memberId) return null;
    const group = 'arrears-' + memberId;
    return (allInvoices || []).find(
      i => i.invoice_group === group && (i.fee_type || '').toUpperCase() === 'AWTF'
    ) ?? null;
  }, [allInvoices, memberId]);

  const paid = existingInvoice ? isPaid(existingInvoice) : false;
  const mySlip = existingInvoice ? mySlips.find(s => s.reference_nos === existingInvoice.reference_no) : null;
  const slipRejected = !justSubmitted && mySlip?.status === 'rejected';
  const slipSubmitted = justSubmitted || (!!mySlip && !slipRejected);

  const firstYear = years[0]?.year ?? '';
  const lastYear  = years[years.length - 1]?.year ?? '';
  const yearRange = years.length === 1 ? String(firstYear) : `${firstYear}–${lastYear}`;

  async function handleGenerate() {
    setBusy(true); setErr('');
    try {
      await invoiceAPI.generateArrears();
      await onRefresh();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to generate invoice.');
      setBusy(false);
    }
  }

  async function handleDownload() {
    if (!existingInvoice?.id) return;
    setDlBusy(true); setErr('');
    try { await invoiceAPI.openPdf(existingInvoice.id); }
    catch (e) { setErr(e.message || 'Download failed.'); }
    finally { setDlBusy(false); }
  }

  async function handleViewReceipt() {
    if (!existingInvoice?.id) return;
    setDlBusy(true); setErr('');
    try { await invoiceAPI.openReceipt(existingInvoice.id); }
    catch (e) { setErr(e.message || 'Download failed.'); }
    finally { setDlBusy(false); }
  }

  return (
    <div className={`overflow-hidden rounded-[22px] border bg-white shadow-sm ${paid ? 'border-emerald-200' : 'border-rose-200'}`}>

      {/* Top bar */}
      <div className={`flex items-center gap-3 px-5 py-4 ${paid ? 'bg-emerald-600' : 'bg-rose-600'}`}>
        <div className="h-8 w-8 shrink-0 rounded-full bg-white/20 flex items-center justify-center">
          {paid ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="h-4 w-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="h-4 w-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-widest ${paid ? 'text-emerald-200' : 'text-rose-200'}`}>Annual Contributions</p>
          <p className="text-sm font-bold text-white leading-tight">
            {paid ? 'Fees Arrears — Paid' : `Fees Arrears — ${yearRange}`}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className={`text-xs ${paid ? 'text-emerald-200' : 'text-rose-200'}`}>TZS</p>
          <p className="text-xl font-black text-white leading-none">
            {Number(paid ? existingInvoice.fee : total_debt).toLocaleString()}
          </p>
        </div>
      </div>

      {!paid && (
      <>
      {/* Meta row */}
      <div className="flex items-center justify-between px-5 py-2.5 bg-rose-50 border-b border-rose-100">
        <p className="text-xs text-rose-700">
          Last paid: <span className="font-semibold">{fees_year}</span>
          <span className="mx-2 text-rose-300">·</span>
          {years.length} year{years.length !== 1 ? 's' : ''} outstanding
        </p>
        <button
          onClick={() => setOpen(v => !v)}
          className="flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-800 transition-colors"
        >
          {open ? 'Hide' : 'View breakdown'}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>

      {/* Year grid — collapsible */}
      {open && (
        <div className="grid grid-cols-2 gap-px bg-rose-100/60 border-b border-rose-100">
          {years.map(({ year, amount }) => (
            <div key={year} className="flex items-center justify-between bg-white px-4 py-2.5">
              <span className="text-sm font-semibold text-slate-700">{year}</span>
              <span className="text-sm text-rose-600 font-bold">{Number(amount).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {/* Action area */}
      <div className="px-5 py-4">
        {err && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>
        )}

        {paid ? (
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-500 mb-1">Reference No</p>
              <p className="font-mono text-lg font-black text-slate-900 tracking-wider leading-none">{existingInvoice.reference_no}</p>
              <p className="text-xs text-emerald-600 mt-1 font-semibold">✓ Payment confirmed</p>
            </div>
            <button
              onClick={handleViewReceipt}
              disabled={dlBusy}
              className="shrink-0 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60 shadow-sm transition-colors"
            >
              🧾 {dlBusy ? 'Loading…' : 'View Receipt'}
            </button>
          </div>
        ) : existingInvoice ? (
          <>
            <div className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Reference No</p>
                <p className="font-mono text-lg font-black text-slate-900 tracking-wider leading-none">{existingInvoice.reference_no}</p>
                <p className="text-xs text-slate-400 mt-1">Pay via CRDB · TZS {Number(existingInvoice.fee).toLocaleString()}</p>
              </div>
              <button
                onClick={handleDownload}
                disabled={dlBusy}
                className="shrink-0 rounded-2xl bg-slate-800 px-5 py-3 text-sm font-bold text-white hover:bg-slate-900 disabled:opacity-60 shadow-sm transition-colors"
              >
                {dlBusy ? 'Loading…' : '⬇ Invoice'}
              </button>
            </div>
            {payMethods.length > 0 && (
              <button onClick={() => setShowHTP(true)}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50 py-3 text-sm font-semibold text-slate-600 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
                </svg>
                How to Pay
              </button>
            )}
            {depositSlipEnabled && (
              slipSubmitted ? (
                <div className="mt-3 flex items-center justify-center gap-2 text-center text-xs font-semibold text-emerald-600">
                  <span>✓ Deposit slip submitted — awaiting AWTF confirmation.</span>
                  {mySlip?.attachment_path && (
                    <a href={mySlip.attachment_path} target="_blank" rel="noreferrer"
                      className="font-bold text-red-600 underline decoration-red-400 underline-offset-2 hover:text-red-700">
                      View
                    </a>
                  )}
                </div>
              ) : slipRejected ? (
                <div className="mt-3 rounded-xl border-2 border-red-200 bg-red-50 p-3">
                  <p className="text-xs font-bold text-red-700">✕ Deposit slip rejected</p>
                  {mySlip?.rejection_reason && (
                    <p className="mt-1 text-xs text-red-600">Reason: {mySlip.rejection_reason}</p>
                  )}
                  <button onClick={() => setShowSlip(true)}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 py-2.5 text-sm font-semibold text-blue-700 transition-colors">
                    📎 Attach a New Deposit Slip
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowSlip(true)}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 py-3 text-sm font-semibold text-blue-700 transition-colors">
                  📎 Paid via Direct Deposit? Attach Proof
                </button>
              )
            )}
          </>
        ) : (
          <button
            onClick={handleGenerate}
            disabled={busy}
            className="w-full rounded-2xl bg-rose-600 py-3 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-60 shadow-sm transition-colors"
          >
            {busy ? 'Generating…' : `+ Generate Invoice · TZS ${Number(total_debt).toLocaleString()}`}
          </button>
        )}
      </div>

      {showSlip && existingInvoice && (
        <DepositSlipModal
          invoice={existingInvoice}
          onClose={() => setShowSlip(false)}
          onDone={() => { setShowSlip(false); setJustSubmitted(true); }}
        />
      )}
      {showHTP && payMethods.length > 0 && (
        <HowToPayModal methods={payMethods} onClose={() => setShowHTP(false)} />
      )}
    </div>
  );
}

// ─── Current Year Invoice Card ───────────────────────────────────────────────

function CurrentYearCard({ invoice, year, payMethods = [], onGenerate, showGenerate, onRefresh, depositSlipEnabled = true, mySlips = [] }) {
  const [busy, setBusy]       = useState(false);
  const [showHTP, setShowHTP] = useState(false);
  const [showSlip, setShowSlip] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [err, setErr]         = useState('');

  const paid = invoice ? isPaid(invoice) : false;
  const mySlip = invoice ? mySlips.find(s => s.reference_nos === invoice.reference_no) : null;
  const slipRejected = !justSubmitted && mySlip?.status === 'rejected';
  const slipSubmitted = justSubmitted || (!!mySlip && !slipRejected);

  const headerBg = paid ? 'bg-emerald-600' : invoice ? 'bg-slate-700' : 'bg-slate-100';
  const textWhite = paid || invoice;

  async function handleDownload() {
    if (!invoice?.id) return;
    setBusy(true); setErr('');
    try { await invoiceAPI.openPdf(invoice.id); }
    catch (e) { setErr(e.message || 'Download failed.'); }
    finally { setBusy(false); }
  }

  async function handleViewReceipt() {
    if (!invoice?.id) return;
    setBusy(true); setErr('');
    try { await invoiceAPI.openReceipt(invoice.id); }
    catch (e) { setErr(e.message || 'Download failed.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      {showHTP && payMethods.length > 0 && (
        <HowToPayModal methods={payMethods} onClose={() => setShowHTP(false)} />
      )}

      <div className={`rounded-2xl overflow-hidden shadow-sm bg-white ring-1 ${
        paid ? 'ring-emerald-200' : 'ring-slate-200'
      }`}>
        {/* Header */}
        <div className={`px-5 py-4 ${headerBg}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-[10px] font-bold uppercase tracking-widest ${textWhite ? 'text-white/60' : 'text-slate-500'}`}>
                AWTF Welfare Fund
              </p>
              <p className={`mt-0.5 text-base font-bold ${textWhite ? 'text-white' : 'text-slate-600'}`}>
                Year {year}{!invoice && ' — No Invoice Yet'}
              </p>
            </div>
            {invoice && (
              <span className={`rounded-full px-3 py-1 text-xs font-bold tracking-wide ${
                paid ? 'bg-white text-emerald-700' : 'bg-amber-400 text-amber-900'
              }`}>
                {paid ? '✓ Paid' : 'Pending'}
              </span>
            )}
          </div>
          {invoice && (
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-sm font-semibold text-white/60">TZS</span>
              <span className="text-3xl font-black text-white">{Number(invoice.fee || 0).toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-5">
          {err && (
            <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>
          )}

          {!invoice ? (
            showGenerate ? (
              <>
                <p className="mb-4 text-sm text-slate-500">No invoice generated for {year} yet.</p>
                <button onClick={onGenerate}
                  className="w-full rounded-2xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors">
                  + Generate Invoice
                </button>
              </>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">—</p>
            )
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Payment Reference</p>
                <p className="mt-1 font-mono text-xl font-bold tracking-wider text-slate-900">{invoice.reference_no}</p>
              </div>

              <div className="flex gap-6">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Due Date</p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-700">{invoice.duedate || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Fee Type</p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-700">AWTF Welfare</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {paid ? (
                  <button onClick={handleViewReceipt} disabled={busy}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                    🧾 {busy ? 'Loading…' : 'View Receipt'}
                  </button>
                ) : (
                  <>
                    <button onClick={handleDownload} disabled={busy}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-800 py-3 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-60">
                      📄 {busy ? 'Loading…' : 'Download Invoice'}
                    </button>
                    {payMethods.length > 0 && (
                      <button onClick={() => setShowHTP(true)}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50 py-3 text-sm font-semibold text-slate-600 transition-colors">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                          <circle cx="12" cy="12" r="10" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
                        </svg>
                        How to Pay
                      </button>
                    )}
                    {depositSlipEnabled && (
                      slipSubmitted ? (
                        <div className="flex items-center justify-center gap-2 text-center text-xs font-semibold text-emerald-600">
                          <span>✓ Deposit slip submitted — awaiting AWTF confirmation.</span>
                          {mySlip?.attachment_path && (
                            <a href={mySlip.attachment_path} target="_blank" rel="noreferrer"
                              className="font-bold text-red-600 underline decoration-red-400 underline-offset-2 hover:text-red-700">
                              View
                            </a>
                          )}
                        </div>
                      ) : slipRejected ? (
                        <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3">
                          <p className="text-xs font-bold text-red-700">✕ Deposit slip rejected</p>
                          {mySlip?.rejection_reason && (
                            <p className="mt-1 text-xs text-red-600">Reason: {mySlip.rejection_reason}</p>
                          )}
                          <button onClick={() => setShowSlip(true)}
                            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 py-2.5 text-sm font-semibold text-blue-700 transition-colors">
                            📎 Attach a New Deposit Slip
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setShowSlip(true)}
                          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 py-3 text-sm font-semibold text-blue-700 transition-colors">
                          📎 Paid via Direct Deposit? Attach Proof
                        </button>
                      )
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showSlip && invoice && (
        <DepositSlipModal
          invoice={invoice}
          onClose={() => setShowSlip(false)}
          onDone={() => { setShowSlip(false); setJustSubmitted(true); }}
        />
      )}
    </>
  );
}

// ─── Deposit Slip Upload Modal ───────────────────────────────────────────────

function DepositSlipModal({ invoice, onClose, onDone }) {
  const [file, setFile]         = useState(null);
  const [datePaid, setDatePaid] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount]     = useState(invoice.fee);
  const [notes, setNotes]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { setErr('Please attach the deposit slip.'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('attachment', file);
      fd.append('reference_no', invoice.reference_no);
      fd.append('amount', amount);
      fd.append('date_paid', datePaid);
      fd.append('notes', notes);
      await paymentSlipsAPI.upload(fd);
      onDone?.();
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-sm font-bold text-slate-800">Attach Proof of Direct Deposit</h2>
        <p className="mt-1 text-xs text-slate-400">
          Ref: <span className="font-mono">{invoice.reference_no}</span> — AWTF will verify your deposit slip and confirm payment.
        </p>
        {err && <p className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{err}</p>}
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Deposit Slip (image or PDF)</label>
            <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" required
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Amount (TZS)</label>
              <input type="number" required value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date Paid</label>
              <input type="date" required value={datePaid} onChange={(e) => setDatePaid(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Bank name, teller reference"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className="rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-medium">
              {busy ? 'Uploading…' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SummaryPanel({ title, value, tone }) {
  const cfg = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', label: 'text-emerald-600', val: 'text-emerald-900', bar: 'bg-emerald-400' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-100',   label: 'text-amber-600',   val: 'text-amber-900',   bar: 'bg-amber-400' },
  }[tone];
  const parts = value.match(/^(TZS\s*)(.*)$/) || [];
  const number = parts[2] ?? value;
  return (
    <div className={`rounded-2xl border p-3 ${cfg.bg} ${cfg.border}`}>
      <span className={`mb-2 block h-1 w-5 rounded-full ${cfg.bar}`} />
      <p className={`text-[10px] font-bold uppercase tracking-widest ${cfg.label}`}>{title}</p>
      <p className={`text-[10px] font-semibold mt-1.5 ${cfg.label} opacity-70`}>TZS</p>
      <p className={`text-lg font-extrabold leading-none ${cfg.val}`}>{number}</p>
    </div>
  );
}
